import dayjs, { extend as dayjsExtend } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib';
import { useTimeZone } from '../../../utils/time-zone';
import { Text } from '../../ui/text';
import { DateInput } from '../date-input';
import { Input } from '../input';

dayjsExtend(utc);
dayjsExtend(timezone);

type Time = {
  hours: number;
  minutes: number;
};

export type DateTimeInputProps = {
  value?: string;
  onChange?: (value?: string) => void;
  /** Set on both the date and the time input. */
  name?: string;
  label?: string;
  placeholder?: string;
  /** Time a freshly picked date gets. Defaults to midnight. */
  defaultTime?: Time;
  fromDate?: Date;
  toDate?: Date;
  disabled?: boolean;
  className?: string;
};

const DAY_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';

const formatTime = ({ hours, minutes }: Time) =>
  `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

/** A stored instant as the day and time it reads as in `timeZone`. */
export function parseDateTimeInputValue(
  value: string | undefined,
  timeZone: string,
): { day?: string; time?: string } {
  const instant = value ? dayjs(value) : undefined;
  if (!instant?.isValid()) {
    return {};
  }
  const local = instant.tz(timeZone);
  return { day: local.format(DAY_FORMAT), time: local.format(TIME_FORMAT) };
}

/** The typed day and time, read in `timeZone`, as a UTC instant. */
export function formatDateTimeInputValue({
  day,
  time,
  timeZone,
}: {
  day: string;
  time: string;
  timeZone: string;
}): string {
  // Parsing resolves the offset of the typed day; tz() setters would keep today's
  return dayjs.tz(`${day}T${time}`, timeZone).toISOString();
}

// the returned value by onChange is a string or undefined if the date or the time is not set
export function DateTimeInput({
  value,
  onChange,
  name,
  label,
  placeholder,
  defaultTime = { hours: 0, minutes: 0 },
  fromDate,
  toDate,
  disabled,
  className,
}: DateTimeInputProps) {
  const { t } = useTranslation();
  const timeZone = useTimeZone();
  const toFields = (stored?: string) => {
    const { day = '', time = formatTime(defaultTime) } = parseDateTimeInputValue(stored, timeZone);
    return { day, time };
  };
  const [{ day, time }, setFields] = useState(() => toFields(value));
  const lastEmitted = useRef(value);

  // Forms fill values after mount (reset on load, autofill); a value we did not emit wins
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setFields(toFields(value));
    }
  }, [value]);

  const update = (next: { day: string; time: string }) => {
    setFields(next);
    const emitted =
      next.day && next.time ? formatDateTimeInputValue({ ...next, timeZone }) : undefined;
    lastEmitted.current = emitted;
    onChange?.(emitted);
  };

  const toCalendarDay = (date: Date) => dayjs(date).tz(timeZone).format(DAY_FORMAT);

  const offset = (
    day ? dayjs.tz(`${day}T${time || '00:00'}`, timeZone) : dayjs().tz(timeZone)
  ).format('Z');

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <DateInput
        name={name}
        className="w-auto"
        aria-label={label ?? placeholder}
        title={placeholder}
        value={day}
        min={fromDate ? toCalendarDay(fromDate) : undefined}
        max={toDate ? toCalendarDay(toDate) : undefined}
        disabled={disabled}
        onChange={nextDay => update({ day: nextDay ?? '', time })}
      />
      <Input
        type="time"
        name={name}
        className="w-auto"
        aria-label={label ?? placeholder}
        value={time}
        disabled={disabled}
        onChange={({ target }) => update({ day, time: target.value })}
      />
      <Text size="sm" tone="muted" className="whitespace-nowrap">
        {t('dateTimeInput.timezone', { timezone: timeZone, offset })}
      </Text>
    </div>
  );
}
